// RTSP-Kamera (z. B. UniFi Protect), portiert aus prototyp/public/widgets.js.
// Browser können kein RTSP; die Player-App startet go2rtc lokal und stellt eine Brücke bereit,
// die eine WebSocket-Adresse liefert (fragmentiertes MP4 über MSE, ca. 1 s Verzögerung).
// Die Kamera-Adresse enthält ein Geheimnis: nie anzeigen, nie loggen.
import { clear, h, placeholder } from './dom.js';
const CAM_CODECS = ['avc1.640029', 'avc1.64002A', 'avc1.640033', 'hvc1.1.6.L153.B0']; // nur Video, kein Ton
export function rtspCamera(doc, box, el, opts, fit) {
    const name = el.name || 'Kamera';
    if (!el.rtspUrl)
        return placeholder(doc, box, 'Stream-Adresse der Kamera eingeben');
    if (opts.preview)
        return placeholder(doc, box, `📹 ${name}`, 'RTSP / UniFi Protect, Bild nur in der Player-App');
    const bridge = opts.camera;
    const win = doc.defaultView;
    const MS = win?.MediaSource;
    if (!bridge || typeof bridge.stream !== 'function' || !MS)
        return placeholder(doc, box, `📹 ${name}`, 'Nur in der Player-App verfügbar');
    const rtsp = el.rtspUrl;
    clear(box);
    const video = h(doc, 'video', {
        class: 'wl-fill',
        style: `${fit};background:#000`,
        attrs: { muted: true, autoplay: true, playsinline: true, disablepictureinpicture: true },
    });
    video.muted = true;
    const msg = h(doc, 'div', { class: 'wl-cam-msg', text: `${name}: verbinde …` });
    box.append(video, msg);
    const status = (t) => {
        msg.textContent = t ? `${name}: ${t}` : '';
        msg.style.display = t ? '' : 'none';
    };
    let ws = null;
    let ms = null;
    let sb = null;
    let queue = [];
    let closed = false;
    let retry = 0;
    let timer;
    let lastData = 0;
    let lastTime = -1;
    let lastMove = 0;
    const teardown = () => {
        if (ws) {
            ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
            try {
                ws.close();
            }
            catch {
                /* egal */
            }
        }
        ws = null;
        sb = null;
        ms = null;
        queue = [];
        video.removeAttribute('src');
        video.load();
    };
    const again = (reason) => {
        if (closed)
            return;
        teardown();
        clearTimeout(timer);
        const delay = Math.min(30_000, 2000 * 2 ** Math.min(retry++, 4));
        status(`${reason}, neuer Versuch in ${Math.round(delay / 1000)} s`);
        timer = setTimeout(() => void connect(), delay);
    };
    const pump = () => {
        if (!sb || sb.updating)
            return;
        if (queue.length) {
            const len = queue.reduce((n, b) => n + b.byteLength, 0);
            const data = new Uint8Array(len);
            let o = 0;
            for (const b of queue) {
                data.set(new Uint8Array(b), o);
                o += b.byteLength;
            }
            queue = [];
            try {
                sb.appendBuffer(data);
            }
            catch {
                again('Wiedergabefehler');
            }
            return;
        }
        // Live bleiben: alten Puffer wegwerfen, bei Rückstand vorspringen, sonst sanft nachregeln.
        if (!sb.buffered.length)
            return;
        const end = sb.buffered.end(sb.buffered.length - 1);
        const start = sb.buffered.start(0);
        if (end - start > 10) {
            try {
                sb.remove(start, end - 5);
            }
            catch {
                /* egal */
            }
            return;
        }
        const gap = end - video.currentTime;
        if (video.currentTime < start || gap > 4)
            video.currentTime = Math.max(start, end - 0.5);
        else
            video.playbackRate = gap > 1.5 ? 1.25 : gap < 0.3 ? 0.9 : 1;
    };
    async function connect() {
        if (closed)
            return;
        let r;
        try {
            r = await bridge.stream(rtsp);
        }
        catch {
            r = null;
        }
        if (closed)
            return;
        if (!r || !r.ok || !r.ws)
            return again(r?.error || 'Player-App antwortet nicht');
        const wsUrl = r.ws;
        const mySource = new MS();
        ms = mySource;
        video.src = URL.createObjectURL(mySource);
        mySource.addEventListener('sourceopen', () => {
            if (ms !== mySource)
                return;
            URL.revokeObjectURL(video.src);
            const sock = new WebSocket(wsUrl);
            ws = sock;
            sock.binaryType = 'arraybuffer';
            lastData = lastMove = Date.now();
            sock.onopen = () => sock.send(JSON.stringify({
                type: 'mse',
                value: CAM_CODECS.filter((c) => MS.isTypeSupported(`video/mp4; codecs="${c}"`)).join(),
            }));
            sock.onmessage = (ev) => {
                if (typeof ev.data !== 'string') {
                    lastData = Date.now();
                    queue.push(ev.data);
                    return pump();
                }
                let m = {};
                try {
                    m = JSON.parse(ev.data);
                }
                catch {
                    /* egal */
                }
                if (m.type === 'mse') {
                    try {
                        sb = mySource.addSourceBuffer(m.value ?? '');
                        sb.mode = 'segments';
                        sb.addEventListener('updateend', pump);
                    }
                    catch {
                        again('Videoformat wird nicht unterstützt (H.265?)');
                    }
                }
                else if (m.type === 'error') {
                    // Fehlertext von go2rtc kann die Kamera-Adresse enthalten, deshalb nicht anzeigen.
                    again(/codec/i.test(m.value || '') ? 'Videoformat wird nicht unterstützt (H.265?)' : 'Kamera nicht erreichbar');
                }
            };
            sock.onclose = () => again('Verbindung getrennt');
            video.play().catch(() => { });
        }, { once: true });
    }
    video.addEventListener('error', () => again('Wiedergabefehler'));
    video.addEventListener('playing', () => {
        retry = 0;
        status('');
    });
    // Wächter: keine Daten oder stehendes Bild seit 10 s → neu verbinden.
    const watch = setInterval(() => {
        if (!ws || closed)
            return;
        const now = Date.now();
        if (video.currentTime !== lastTime) {
            lastTime = video.currentTime;
            lastMove = now;
        }
        if (now - lastData > 10_000)
            again('keine Daten');
        else if (now - lastMove > 15_000)
            again('Bild steht');
    }, 2000);
    void connect();
    return () => {
        closed = true;
        clearTimeout(timer);
        clearInterval(watch);
        teardown();
    };
}
//# sourceMappingURL=camera.js.map